import ToggleRow from '@components/Customization/ToggleRow';
import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import { useLocalization } from '@lib/localization';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import {
  Alert, Box, Button, Divider, Drawer, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  type SsoAccounts, type SsoClaims, type SsoConfig, type SsoConfigUpdate, type SsoProvider, ssoProviders, type SsoTokenAuthMethod,
  ssoTokenAuthMethods,
} from '@store/api/ssoEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { type ApiFieldError } from '@store/error';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { type FC, useEffect, useState } from 'react';
import { TimesIcon } from 'react-line-awesome';
import SsoAccountsForm from './SsoAccountsForm';
import { errorAt, ssoErrorText, ssoFieldLabel } from './ssoFieldErrors';
import { ssoProviderPresets } from './ssoPresets';

interface Props {
  open: boolean;
  config: SsoConfig;
  saving: boolean;
  /** The fields the server refused the last save or preview for. */
  errors: ApiFieldError[];
  onClose: () => void;
  onSave: (update: SsoConfigUpdate) => void;
}

/** The form's copy of the settings. Text where the field is typed, whatever the server stores. */
interface Draft {
  provider: SsoProvider;
  displayName: string;
  publicUrl: string;
  tenantId: string;
  issuer: string;
  clientId: string;
  /** Empty leaves the stored secret alone: the server never sends it, so there is nothing to show. */
  clientSecret: string;
  /** `yyyy-mm-dd`, or empty for none. */
  clientSecretExpiresAt: string;
  /** Separated by spaces, as OAuth writes them. */
  scopes: string;
  tokenAuthMethod: SsoTokenAuthMethod;
  claims: SsoClaims;
  requireVerifiedEmail: boolean;
  accounts: SsoAccounts;
}

const draftOf = (config: SsoConfig): Draft => ({
  provider: config.provider,
  displayName: config.displayName,
  // A server that was never told is most likely reached the way this page was.
  publicUrl: config.publicUrl || window.location.origin,
  tenantId: config.tenantId ?? '',
  issuer: config.issuer ?? '',
  clientId: config.clientId,
  clientSecret: '',
  clientSecretExpiresAt: config.clientSecretExpiresAt?.slice(0, 10) ?? '',
  scopes: config.scopes.join(' '),
  tokenAuthMethod: config.tokenAuthMethod,
  claims: { ...config.claims },
  requireVerifiedEmail: config.requireVerifiedEmail,
  accounts: {
    ...config.accounts,
    defaultTeams: config.accounts.defaultTeams.map((team) => ({ ...team })),
    mappings: config.accounts.mappings.map((rule) => ({ ...rule })),
  },
});

/**
 * Only what differs from what is stored, since the API leaves a part the body does not carry
 * as it is: a field nobody touched is not written back from a copy read when the form opened.
 */
const updateOf = (draft: Draft, config: SsoConfig): SsoConfigUpdate => {
  const stored = draftOf(config);
  const update: SsoConfigUpdate = {};
  const scopes = draft.scopes.split(/\s+/).filter(Boolean);

  if (draft.provider !== stored.provider) update.provider = draft.provider;
  if (draft.displayName !== stored.displayName) update.displayName = draft.displayName;
  // Against the stored value and not the form's first guess, so that the guess is saved.
  if (draft.publicUrl !== config.publicUrl) update.publicUrl = draft.publicUrl;
  if (draft.tenantId && draft.tenantId !== stored.tenantId) update.tenantId = draft.tenantId;
  if (draft.issuer && draft.issuer !== stored.issuer) update.issuer = draft.issuer;
  if (draft.clientId !== stored.clientId) update.clientId = draft.clientId;
  if (draft.clientSecret) update.clientSecret = draft.clientSecret;
  if (draft.clientSecretExpiresAt !== stored.clientSecretExpiresAt) {
    update.clientSecretExpiresAt = draft.clientSecretExpiresAt || null;
  }
  if (scopes.join(' ') !== stored.scopes) update.scopes = scopes;
  if (draft.tokenAuthMethod !== stored.tokenAuthMethod) update.tokenAuthMethod = draft.tokenAuthMethod;
  if (JSON.stringify(draft.claims) !== JSON.stringify(stored.claims)) update.claims = draft.claims;
  if (draft.requireVerifiedEmail !== stored.requireVerifiedEmail) update.requireVerifiedEmail = draft.requireVerifiedEmail;
  if (JSON.stringify(draft.accounts) !== JSON.stringify(stored.accounts)) update.accounts = draft.accounts;

  return update;
};

// The space below is for the first field's label, which is drawn on the field's upper edge.
// It only takes on an element that is not a `p`: `index.css` sets a paragraph's lower margin to
// nothing, so the titles are rendered as a `div`.
const sectionTitleStyles = { fontWeight: 700, fontSize: 13, letterSpacing: 0.3, color: 'text.secondary', mb: 2.5 };

/** What the server would refuse anyway, caught before the request: the reader is still typing. */
const draftIssues = (draft: Draft): ApiFieldError[] => [
  // The server refuses it on the value; the role and the team are what is missing.
  ...draft.accounts.mappings.flatMap((rule, index) => (rule.role || rule.teamId
    ? []
    : [{ path: `accounts.mappings.${index}.role`, code: 'rule_gives_nothing', message: 'A rule has to give a role, a team, or both' }])),
  ...(ssoProviderPresets[draft.provider].advanced && !draft.scopes.split(/\s+/).includes('openid')
    ? [{ path: 'scopes', code: 'openid_required', message: 'The scopes have to include openid' }]
    : []),
  ...draft.accounts.mappings.flatMap((rule, index) => (rule.value.trim()
    ? []
    : [{ path: `accounts.mappings.${index}.value`, code: 'too_small', message: 'A rule needs a value' }])),
];

/**
 * The single sign-on settings as a form: how the provider is reached, then who gets in and as
 * what. It is saved in one request, and stays open when the server refuses it, with what was
 * typed still in it and each refusal on its field. Closing it with changes not saved asks first.
 */
const SsoDrawer: FC<Props> = ({ open, config, saving, errors, onClose, onSave }) => {
  const { t } = useLocalization();
  const dispatch = useAppDispatch();
  const { data: teams } = useGetTeamsQuery(undefined, { skip: !open });
  const [draft, setDraft] = useState<Draft>(() => draftOf(config));
  // The draft as the drawer opened with it, guesses included: what closing it would lose.
  const [opened, setOpened] = useState<Draft>(() => draftOf(config));
  // The refusals still shown: one goes once its field is changed.
  const [shown, setShown] = useState<ApiFieldError[]>([]);
  const [isAskingToDiscard, setAskingToDiscard] = useState(false);

  // The draft starts from the stored settings each time the drawer is opened, and not when
  // they are read again under it: a save that was refused leaves what was typed.
  useEffect(() => {
    if (!open) return;

    const fresh = draftOf(config);

    setDraft(fresh);
    setOpened(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => setShown(errors), [errors]);

  const forget = (paths: string[]) => setShown((current) => current.filter((error) => !paths.some((path) =>
    error.path === path || error.path.startsWith(`${path}.`))));
  const set = (part: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...part }));
    // Another provider shows other fields: a refusal of one it no longer shows goes too.
    forget([...Object.keys(part), ...(part.provider ? ['tenantId', 'issuer', 'tokenAuthMethod', 'scopes', 'claims'] : [])]);
  };
  const setClaim = (part: Partial<SsoClaims>) => {
    setDraft((current) => ({ ...current, claims: { ...current.claims, ...part } }));
    forget(Object.keys(part).map((claim) => `claims.${claim}`));
  };

  const update = updateOf(draft, config);
  const isDirty = Object.keys(update).length > 0;
  const preset = ssoProviderPresets[draft.provider];

  /** The refusal of a field, as its helper text says it. */
  const errorText = (path: string, options?: { exact?: boolean }) => {
    const error = errorAt(shown, path, options);

    return error ? ssoErrorText(t, error) : undefined;
  };

  // Where a refusal has a field on screen to be shown on; the rest are listed at the top.
  const isInPlace = (path: string) => {
    const [root, part, index, leaf] = path.split('.');
    const { assignment, autoCreate } = draft.accounts;

    switch (root) {
      case 'provider': case 'displayName': case 'publicUrl': case 'clientId': case 'clientSecret':
      case 'clientSecretExpiresAt':
        return true;
      case 'tenantId': case 'issuer':
        return preset.names === root;
      case 'tokenAuthMethod': case 'scopes': case 'requireVerifiedEmail':
        return preset.advanced;
      case 'claims':
        return preset.advanced && part !== undefined;
      case 'accounts':
        if (part === 'mappingClaim') return assignment === 'mapping';
        if (part === 'mappings') return assignment === 'mapping' && (index === undefined || leaf !== undefined);
        if (part === 'defaultTeams') return assignment === 'default' && autoCreate && (index === undefined || leaf !== undefined);
        return false;
      default:
        return false;
    }
  };
  const elsewhere = shown.filter((error) => !isInPlace(error.path));

  // Asked only when something was typed: the address guessed for a server that was never told
  // one makes the form savable, but closing it loses nothing.
  const close = () => (JSON.stringify(draft) !== JSON.stringify(opened) ? setAskingToDiscard(true) : onClose());

  const submit = () => {
    const issues = draftIssues(draft);

    if (issues.length > 0) setShown(issues);
    else onSave(update);
  };

  const redirectUri = draft.publicUrl ? `${draft.publicUrl.replace(/\/+$/, '')}/api/v1/sso/callback` : '';

  const copyRedirectUri = async () => {
    await navigator.clipboard.writeText(redirectUri);
    dispatch(showSnackbar({ severity: 'info', message: t('Common_Copied') }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : close}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 560 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>{t('ServerConfig_Sso_Edit')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('ServerConfig_Sso_Edit_Subtitle')}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('Common_Close')} onClick={close} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          {elsewhere.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {t('ServerConfig_Sso_Refused')}
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {elsewhere.map((error) => (
                  <li key={`${error.path}:${error.code ?? error.message}`}>
                    {ssoFieldLabel(t, error.path)}: {ssoErrorText(t, error)}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          <Typography component="div" sx={sectionTitleStyles}>{t('ServerConfig_Sso_Section_Connection')}</Typography>
          <Stack spacing={2}>
            <TextField
              select fullWidth size="small"
              label={t('ServerConfig_Sso_Provider')}
              value={draft.provider}
              onChange={(event) => set({ provider: event.target.value as SsoProvider })}
              disabled={saving}
            >
              {ssoProviders.map((provider) => (
                <MenuItem key={provider} value={provider}>{t(ssoProviderPresets[provider].label)}</MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth size="small"
              label={t('ServerConfig_Sso_DisplayName')}
              error={Boolean(errorText('displayName'))}
              helperText={errorText('displayName') ?? t('ServerConfig_Sso_DisplayName_Description')}
              value={draft.displayName}
              onChange={(event) => set({ displayName: event.target.value })}
              disabled={saving}
            />

            <TextField
              fullWidth size="small"
              label={t('ServerConfig_Sso_PublicUrl')}
              error={Boolean(errorText('publicUrl'))}
              helperText={errorText('publicUrl') ?? t('ServerConfig_Sso_PublicUrl_Description')}
              placeholder="https://insight.example.com"
              value={draft.publicUrl}
              onChange={(event) => set({ publicUrl: event.target.value.trim() })}
              disabled={saving}
            />

            <TextField
              fullWidth size="small"
              label={t('ServerConfig_Sso_RedirectUri')}
              helperText={t('ServerConfig_Sso_RedirectUri_Description')}
              value={redirectUri}
              slotProps={{
                input: {
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: 13 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label={t('ServerConfig_Sso_Copy')} onClick={() => void copyRedirectUri()} disabled={!redirectUri}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {preset.names === 'tenantId'
              ? (
                <TextField
                  fullWidth size="small"
                  label={t('ServerConfig_Sso_TenantId')}
                  error={Boolean(errorText('tenantId'))}
                  helperText={errorText('tenantId') ?? t('ServerConfig_Sso_TenantId_Description')}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={draft.tenantId}
                  onChange={(event) => set({ tenantId: event.target.value.trim() })}
                  disabled={saving}
                />
              )
              : (
                <TextField
                  fullWidth size="small"
                  label={t('ServerConfig_Sso_Issuer')}
                  error={Boolean(errorText('issuer'))}
                  helperText={errorText('issuer') ?? t('ServerConfig_Sso_Issuer_Description')}
                  placeholder="https://idp.example.com/realms/company"
                  value={draft.issuer}
                  onChange={(event) => set({ issuer: event.target.value.trim() })}
                  disabled={saving}
                />
              )}

            <TextField
              fullWidth size="small"
              label={t('ServerConfig_Sso_ClientId')}
              error={Boolean(errorText('clientId'))}
              helperText={errorText('clientId')}
              value={draft.clientId}
              onChange={(event) => set({ clientId: event.target.value.trim() })}
              disabled={saving}
            />

            <TextField
              fullWidth size="small"
              type="password"
              autoComplete="new-password"
              label={t('ServerConfig_Sso_ClientSecret')}
              error={Boolean(errorText('clientSecret'))}
              helperText={errorText('clientSecret') ?? t(config.clientSecret === 'none'
                ? 'ServerConfig_Sso_ClientSecret_Description_None'
                : 'ServerConfig_Sso_ClientSecret_Description_Set')}
              placeholder={config.clientSecret === 'none' ? '' : '••••••••••••'}
              value={draft.clientSecret}
              // A new secret has a date of its own: the old one's is not kept for it.
              // Emptied again, the stored secret stays, and with it its date.
              onChange={(event) => set({
                clientSecret: event.target.value,
                ...(event.target.value && !draft.clientSecret ? { clientSecretExpiresAt: '' } : {}),
                ...(!event.target.value && draft.clientSecret && !draft.clientSecretExpiresAt
                  ? { clientSecretExpiresAt: opened.clientSecretExpiresAt }
                  : {}),
              })}
              disabled={saving}
            />

            {config.clientSecret === 'unreadable' && (
              <Alert severity="warning">{t('ServerConfig_Sso_Secret_Unreadable_Description')}</Alert>
            )}

            <TextField
              fullWidth size="small"
              type="date"
              label={t('ServerConfig_Sso_ClientSecretExpiresAt')}
              error={Boolean(errorText('clientSecretExpiresAt'))}
              helperText={errorText('clientSecretExpiresAt') ?? t(draft.clientSecret
                ? 'ServerConfig_Sso_ClientSecretExpiresAt_New'
                : 'ServerConfig_Sso_ClientSecretExpiresAt_Description')}
              value={draft.clientSecretExpiresAt}
              onChange={(event) => set({ clientSecretExpiresAt: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
            />

            {preset.guide && <Alert severity="info" sx={{ whiteSpace: 'pre-line' }}>{t(preset.guide)}</Alert>}

            {preset.advanced && (
              <>
                <TextField
                  select fullWidth size="small"
                  label={t('ServerConfig_Sso_TokenAuthMethod')}
                  helperText={t('ServerConfig_Sso_TokenAuthMethod_Description')}
                  value={draft.tokenAuthMethod}
                  onChange={(event) => set({ tokenAuthMethod: event.target.value as SsoTokenAuthMethod })}
                  disabled={saving}
                >
                  {ssoTokenAuthMethods.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
                </TextField>

                <TextField
                  fullWidth size="small"
                  label={t('ServerConfig_Sso_Scopes')}
                  error={Boolean(errorText('scopes'))}
                  helperText={errorText('scopes') ?? t('ServerConfig_Sso_Scopes_Description')}
                  value={draft.scopes}
                  onChange={(event) => set({ scopes: event.target.value })}
                  disabled={saving}
                />

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('ServerConfig_Sso_Claims')}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {t('ServerConfig_Sso_Claims_Description')}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
                    {(['subject', 'email', 'name', 'username'] as const).map((claim) => (
                      <TextField
                        key={claim}
                        size="small"
                        sx={{ width: 'calc(50% - 6px)' }}
                        label={t(`ServerConfig_Sso_Claim_${claim.capitalize()}`)}
                        error={Boolean(errorText(`claims.${claim}`))}
                        helperText={errorText(`claims.${claim}`)}
                        value={draft.claims[claim]}
                        onChange={(event) => setClaim({ [claim]: event.target.value.trim() })}
                        disabled={saving}
                      />
                    ))}
                  </Stack>
                </Box>

                <ToggleRow
                  label={t('ServerConfig_Sso_RequireVerifiedEmail')}
                  description={t('ServerConfig_Sso_RequireVerifiedEmail_Description')}
                  checked={draft.requireVerifiedEmail}
                  onChange={(requireVerifiedEmail) => set({ requireVerifiedEmail })}
                  disabled={saving}
                />
              </>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography component="div" sx={{ ...sectionTitleStyles, mb: 1.5 }}>{t('ServerConfig_Sso_Section_Accounts')}</Typography>
          <SsoAccountsForm
            accounts={draft.accounts}
            teams={teams ?? []}
            teamsLoaded={teams !== undefined}
            disabled={saving}
            errorAt={(path, options) => errorText(path, options)}
            onChange={(accounts) => set({ accounts })}
          />
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={drawerFooterStyles}>
          <Button color="inherit" size="small" onClick={close} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={!isDirty || saving}
            onClick={submit}
            sx={buttonStyles}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={isAskingToDiscard}
        title={t('Customization_Designer_Unsaved')}
        confirmLabel={t('ServerConfig_Sso_Discard')}
        confirmColor="warning"
        onClose={() => setAskingToDiscard(false)}
        onConfirm={onClose}
      >
        <Typography variant="body2" color="text.secondary">{t('ServerConfig_Sso_Discard_Content')}</Typography>
      </ConfirmDialog>
    </Drawer>
  );
};

export default SsoDrawer;
